import { perfilDe, type Me, type Perfil } from '@supply/contracts'
import { Eye, LogOut } from 'lucide-react'
import * as React from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { endViewAs, startViewAs, type Reference } from '@/data/api'
import { isMockActive, VIEW_AS_MOCK_BLOCKED } from '@/data/mock/mode'
import type { Member } from '@/data/types'
import { useSession } from '@/lib/session'
import {
  clearViewAs,
  getViewAs,
  saveViewAs,
  VIEW_AS_EXPIRED_EVENT,
  viewAsRemainingMs,
  type ViewAs,
} from '@/lib/view-as'

/**
 * "Ver como" na tela (ADR 0007): faixa fixa no topo enquanto o modo vale,
 * seletor com busca (menu do usuário) e a ação por linha em Usuários e
 * Permissões. A barreira é o banco; aqui é só entrar, mostrar e sair.
 */

const PAPEL: Record<Perfil, string> = {
  admin: 'Admin',
  gestor: 'Gestor',
  responsavel: 'Gerente',
  executor: 'Executor',
  leitura: 'Leitura',
}

/** "Gerente · COMPRAS", "Executor · ESTOQUE", "Gestor". */
export function viewAsLabel(memberId: string, ref: Reference): string {
  const me: Me = {
    memberId,
    roles: ref.roles.filter((r) => r.member_id === memberId).map((r) => r.role),
    areas: ref.areaMembers.filter((am) => am.member_id === memberId).map((am) => ({ areaId: am.area_id, role: am.role })),
  }
  const areas = me.areas
    .map((a) => ref.areas.find((x) => x.id === a.areaId)?.code)
    .filter((c): c is string => !!c)
  return [PAPEL[perfilDe(me)], ...areas].join(' · ')
}

/** Quem o gestor pode escolher: ficha ativa com login no SSO, menos ele mesmo. */
export function canPickViewAs(m: Member, selfId: string): boolean {
  return m.id !== selfId && m.is_active && !!m.user_id
}

/** Abre a sessão no banco (só gestor real) e recarrega a aba já no modo. */
export function useStartViewAs() {
  const { ref } = useSession()
  const [pending, setPending] = React.useState(false)
  const start = React.useCallback(
    async (m: Member) => {
      if (pending) return
      setPending(true)
      const r = await startViewAs(m.id)
      if (!r.ok) {
        setPending(false)
        toast.error('Não foi possível entrar no modo visualização', { description: r.reason })
        return
      }
      saveViewAs({
        sessionId: r.value.id,
        targetMemberId: r.value.target_member_id,
        targetUserId: r.value.target_user_id,
        targetName: m.full_name,
        targetLabel: viewAsLabel(m.id, ref),
        expiresAt: r.value.expires_at,
      })
      window.location.assign('/')
    },
    [pending, ref],
  )
  return { start, pending }
}

/** Sai do modo: encerra no banco (vale também com o header) e volta à própria visão, sem novo login. */
export async function leaveViewAs(viewAs: ViewAs, reason: 'saiu' | 'expirou' = 'saiu') {
  await endViewAs(viewAs.sessionId, reason).catch(() => undefined)
  clearViewAs()
  window.location.assign('/')
}

function formatRemaining(ms: number): string {
  const total = Math.ceil(ms / 1000)
  const min = Math.floor(total / 60)
  const sec = total % 60
  return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
}

/** Faixa de alerta, fixa no topo e sem fechar: some só saindo do modo. */
export function ViewAsBanner() {
  const [viewAs] = React.useState(getViewAs)
  const [now, setNow] = React.useState(() => Date.now())
  const leaving = React.useRef(false)

  const leave = React.useCallback(
    (reason: 'saiu' | 'expirou') => {
      if (!viewAs || leaving.current) return
      leaving.current = true
      if (reason === 'expirou') toast.info('O modo visualização expirou', { description: 'Voltando à sua visão.' })
      void leaveViewAs(viewAs, reason)
    },
    [viewAs],
  )

  React.useEffect(() => {
    if (!viewAs) return
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    const onExpired = () => leave('expirou')
    window.addEventListener(VIEW_AS_EXPIRED_EVENT, onExpired)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener(VIEW_AS_EXPIRED_EVENT, onExpired)
    }
  }, [viewAs, leave])

  // Botão de escrita fica desabilitado (useWrite) e diz por quê ao passar o mouse.
  React.useEffect(() => {
    if (!viewAs) return
    const hint = () => {
      for (const el of document.querySelectorAll<HTMLElement>('button:disabled:not([title])')) {
        el.title = 'Modo visualização'
        el.dataset.viewAsHint = ''
      }
      // Reabilitou (ex.: pending de outra coisa): o título não é mais verdade.
      for (const el of document.querySelectorAll<HTMLButtonElement>('button[data-view-as-hint]:enabled')) {
        el.removeAttribute('title')
        delete el.dataset.viewAsHint
      }
    }
    hint()
    const observer = new MutationObserver(hint)
    observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled', 'data-disabled'] })
    return () => observer.disconnect()
  }, [viewAs])

  const remaining = viewAs ? viewAsRemainingMs(viewAs, now) : 0
  React.useEffect(() => {
    if (viewAs && remaining === 0) leave('expirou')
  }, [viewAs, remaining, leave])

  if (!viewAs) return null
  return (
    <div
      role="status"
      aria-live="polite"
      className="z-40 flex min-h-10 shrink-0 flex-wrap items-center gap-x-3 gap-y-1 bg-warning px-3 py-1.5 text-[13px] text-warning-foreground sm:px-4"
    >
      <Eye className="h-4 w-4 shrink-0" aria-hidden />
      <span className="min-w-0 flex-1">
        Vendo como <strong className="font-semibold">{viewAs.targetName}</strong>
        {viewAs.targetLabel && <span> · {viewAs.targetLabel}</span>}
        <span className="opacity-80"> — modo visualização, somente leitura</span>
      </span>
      <span className="font-mono text-[12px] tabular-nums opacity-90" title="Tempo até o modo expirar">
        expira em {formatRemaining(remaining)}
      </span>
      <Button
        size="sm"
        variant="outline"
        className="h-7 border-warning-foreground/40 bg-transparent text-warning-foreground hover:bg-warning-foreground/10 hover:text-warning-foreground"
        onClick={() => leave('saiu')}
      >
        <LogOut /> Sair
      </Button>
    </div>
  )
}

/** Seletor com busca: menu do usuário (gestor). */
export function ViewAsDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { ref, member: self } = useSession()
  const { start, pending } = useStartViewAs()
  const people = ref.members.filter((m) => canPickViewAs(m, self.id))
  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Ver o Supply como… (nome, e-mail ou área)" disabled={pending} />
      <CommandList>
        <CommandEmpty>Ninguém encontrado.</CommandEmpty>
        <CommandGroup heading="Somente leitura, por 30 minutos">
          {people.map((m) => {
            const label = viewAsLabel(m.id, ref)
            return (
              <CommandItem
                key={m.id}
                value={`${m.full_name} ${m.email} ${label}`}
                disabled={pending}
                onSelect={() => void start(m)}
              >
                <Eye className="mr-2 h-4 w-4" />
                <span className="min-w-0 flex-1 truncate">{m.full_name}</span>
                <span className="ml-2 truncate text-xs text-muted-foreground">{label}</span>
              </CommandItem>
            )
          })}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  )
}

/** Ação "Ver como" de uma linha de Usuários e Permissões. */
export function ViewAsRowButton({ member }: { member: Member }) {
  const { start, pending } = useStartViewAs()
  const mock = isMockActive()
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={mock ? 0 : -1} className="inline-flex">
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Ver como ${member.full_name}`}
            disabled={pending || mock}
            onClick={() => void start(member)}
          >
            <Eye /> <span className="hidden xl:inline">Ver como</span>
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent>{mock ? VIEW_AS_MOCK_BLOCKED : `Ver o Supply como ${member.full_name} (somente leitura, 30 min)`}</TooltipContent>
    </Tooltip>
  )
}
